/**
 * Offline Status Bar - Shows offline status and sync controls
 */

import React from 'react';
import { useOffline } from '../contexts/OfflineContext';
import { Wifi, WifiOff, RefreshCw, Download, Cloud, CloudOff } from 'lucide-react';
import { Button } from './ui/button';

const OfflineStatusBar = () => {
  const {
    isOnline,
    pendingCount,
    isSyncing,
    isDownloading,
    lastSyncTime,
    manualSync,
    downloadOfflineData
  } = useOffline();

  // Don't show if online and nothing pending
  if (isOnline && pendingCount === 0 && !isSyncing) {
    return null;
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 px-4 py-2 flex items-center justify-between text-sm ${
      isOnline ? 'bg-blue-500 text-white' : 'bg-orange-500 text-white'
    }`}>
      <div className="flex items-center gap-2">
        {isOnline ? (
          <>
            <Wifi className="h-4 w-4" />
            <span>Online</span>
          </>
        ) : (
          <>
            <WifiOff className="h-4 w-4" />
            <span>Offline Mode</span>
          </>
        )}
        
        {pendingCount > 0 && (
          <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">
            {pendingCount} pending
          </span>
        )}
        
        {lastSyncTime && (
          <span className="ml-2 text-xs opacity-75">
            Last sync: {lastSyncTime.toLocaleTimeString()}
          </span>
        )}
      </div>
      
      <div className="flex items-center gap-2">
        {isOnline && (
          <>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-white hover:bg-white/20"
              onClick={downloadOfflineData}
              disabled={isDownloading}
            >
              <Download className={`h-4 w-4 mr-1 ${isDownloading ? 'animate-bounce' : ''}`} />
              {isDownloading ? 'Downloading...' : 'Save Offline'}
            </Button>
            
            {pendingCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-white hover:bg-white/20"
                onClick={manualSync}
                disabled={isSyncing}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isSyncing ? 'animate-spin' : ''}`} />
                {isSyncing ? 'Syncing...' : 'Sync Now'}
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Compact version for header/nav
export const OfflineIndicator = () => {
  const { isOnline, pendingCount, isSyncing, manualSync } = useOffline();

  return (
    <div className="flex items-center gap-1">
      {isOnline ? (
        <Cloud className="h-4 w-4 text-green-500" />
      ) : (
        <CloudOff className="h-4 w-4 text-orange-500" />
      )}
      
      {pendingCount > 0 && (
        <button
          onClick={manualSync}
          disabled={!isOnline || isSyncing}
          className="flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs hover:bg-orange-200 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${isSyncing ? 'animate-spin' : ''}`} />
          {pendingCount}
        </button>
      )}
    </div>
  );
};

export default OfflineStatusBar;
